import React from 'react';
import {
  FaUserTie,
  FaUsers,
  FaHourglassHalf,
  FaCheckDouble,
  FaCalendarCheck,
  FaProjectDiagram,
  FaBuilding,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaCog,
  FaFileAlt,
  FaMoneyBillWave,
  FaTruck,
  FaTools,
  FaGraduationCap,
  FaReceipt,
} from 'react-icons/fa';
import { StatCard, SectionCard, MiniStat, StatusBadge, DataTable, formatDate } from './ui';
import { ApprovalsSection } from './approvals-section';
import { BarChart } from './charts';

/**
 * Team Coordinator — NEW spec sections ("Dashboard 140626 Mukul", TC tab).
 *
 * This is a COMPANION component (not a replacement). Your existing `TeamCoordinatorView`
 * is large and already renders engineer attendance / kms / today_work etc., so render this
 * alongside it inside the role switch:
 *
 *     case ROLE_IDS.TEAM_COORDINATOR:
 *       return (
 *         <>
 *           {dashboardData.my_attendance && <PersonalDashboard ... />}
 *           <TeamCoordinatorSpecSections data={dashboardData} />
 *           <TeamCoordinatorView data={dashboardData as unknown as TeamCoordinatorDashboard} />
 *         </>
 *       );
 *
 * Keep this file in the same `components/role-dashboards` folder so './ui' / './charts'
 * resolve. Reads the sections added by `getTeamCoordinatorSpecSections`; renders nothing
 * for any section the backend hasn't provided yet.
 */

const toNumber = (v: unknown) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
};

const PENDING_LABELS: Record<string, string> = {
  site_created: 'Site Created',
  engineer_assigned: 'Engineer Assigned',
  field_work_in_progress: 'Field Work In Progress',
  data_uploaded: 'Site Data WIP',
  field_work_completed: 'Field Data Upload Pending',
  ta_review_pending: 'TA Review Pending',
};
const PENDING_STATUSES = Object.keys(PENDING_LABELS);
const ENGINEER_PENDING_STATUSES = ['engineer_assigned', 'field_work_in_progress', 'data_uploaded', 'field_work_completed'];

// pre-pivoted progress output keys (from buildProgress) → labels
const PROGRESS_LABELS: Record<string, string> = {
  field_work_in_progress: 'Field Work In Progress',
  site_data_wip: 'Site Data WIP',
  field_work_completed: 'Survey Done',
  ta_review_completed: 'Field Work Approved',
  report_completed: 'Report Completed',
  report_finalized: 'Report Finalized',
  report_sent_to_client: 'Report Sent',
};
const PROGRESS_KEYS = Object.keys(PROGRESS_LABELS);
const ENGINEER_PROGRESS_KEYS = ['field_work_in_progress', 'site_data_wip', 'field_work_completed'];

const REJECT_LABELS: Record<string, string> = {
  ta_rejected: 'TA Rejected',
  report_maker_rejected: 'Report Maker Rejected',
  validation_rejected: 'Validation Rejected',
};
const REJECT_STATUSES = Object.keys(REJECT_LABELS);

// pivot flat grouped rows ({ '<name>': x, survey_workflow_status, count }) → one row/dim
const pivotLevel = (rows: any[] | undefined, nameKeys: string[], statuses: string[]) => {
  const map = new Map<string, { name: string } & Record<string, number>>();
  for (const r of rows || []) {
    const name = nameKeys.map((k) => r?.[k]).find((v) => v != null && v !== '') || 'Unassigned';
    const key = String(name);
    const existing = map.get(key) || ({ name: key } as { name: string } & Record<string, number>);
    existing[String(r?.survey_workflow_status ?? '')] = (existing[String(r?.survey_workflow_status ?? '')] || 0) + toNumber(r?.count);
    map.set(key, existing);
  }
  return Array.from(map.values()).map((row) => {
    statuses.forEach((s) => {
      if (row[s] == null) row[s] = 0;
    });
    return row;
  });
};

const flatCols = (firstLabel: string, keys: string[], labels: Record<string, string>) => [
  { key: 'name', label: firstLabel },
  ...keys.map((s) => ({ key: s, label: labels[s] ?? s, align: 'center' as const })),
];
const pivotedCols = (firstKey: string, firstLabel: string, keys: string[], labels: Record<string, string>) => [
  { key: firstKey, label: firstLabel },
  ...keys.map((s) => ({ key: s, label: labels[s] ?? s, align: 'center' as const })),
];

interface Props {
  data: any;
}

export const TeamCoordinatorSpecSections: React.FC<Props> = ({ data }) => {
  const d = data || {};
  const responsibility = d.responsibility_overview;
  const approvals = d.approvals;
  const pendingWork = d.pending_work;
  const todayProgress = d.today_progress;
  const yesterdayProductivity = d.yesterday_productivity;
  const rejectionPending = d.rejection_pending;
  const referenceLists = d.reference_lists;

  const pendingActivity = pivotLevel(pendingWork?.activity_level, ['activity.activity_name'], PENDING_STATUSES);
  const pendingProject = pivotLevel(pendingWork?.project_level, ['project.project_short_name', 'project.project_name'], PENDING_STATUSES);
  const pendingEngineer = pivotLevel(pendingWork?.user_level?.engineer_wise, ['site_engineer_username'], ENGINEER_PENDING_STATUSES);
  const rejectActivity = pivotLevel(rejectionPending?.activity_level, ['activity.activity_name'], REJECT_STATUSES);
  const rejectProject = pivotLevel(rejectionPending?.project_level, ['project.project_short_name', 'project.project_name'], REJECT_STATUSES);

  const renderProgress = (block: any, title: string, icon: any) => {
    if (!block) return null;
    const gs = block.group_stage || {};
    const tc = block.user_level?.tc_wise || {};
    return (
      <div className="space-y-6">
        <SectionCard title={`${title} — Group Stage`} subtitle={formatDate(block.date)} icon={icon}>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MiniStat label="Survey Done" value={gs.survey_done ?? 0} color="blue" />
            <MiniStat label="Field Work Approved" value={gs.field_work_approved ?? 0} color="green" />
            <MiniStat label="Report Completed" value={gs.report_completed ?? 0} color="yellow" />
            <MiniStat label="Report Finalized" value={gs.report_finalized ?? 0} color="green" />
            <MiniStat label="Report Sent" value={gs.report_sent ?? 0} color="green" />
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MiniStat label="Sites Created" value={tc.site_created ?? 0} color="gray" />
            <MiniStat label="Engineers Assigned" value={tc.engineer_assigned ?? 0} color="gray" />
            <MiniStat label="TA Reviews Approved" value={tc.ta_review_completed ?? 0} color="gray" />
          </div>
        </SectionCard>

        {((block.activity_level?.length ?? 0) > 0 || (block.project_level?.length ?? 0) > 0) && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            {(block.activity_level?.length ?? 0) > 0 && (
              <SectionCard title={`${title} — Activity Level`} icon={FaProjectDiagram}>
                <DataTable columns={pivotedCols('activity_name', 'Activity', PROGRESS_KEYS, PROGRESS_LABELS)} data={block.activity_level} maxHeight="300px" emptyMessage="No progress" />
              </SectionCard>
            )}
            {(block.project_level?.length ?? 0) > 0 && (
              <SectionCard title={`${title} — Project Level`} icon={FaBuilding}>
                <DataTable columns={pivotedCols('project_name', 'Project', PROGRESS_KEYS, PROGRESS_LABELS)} data={block.project_level} maxHeight="300px" emptyMessage="No progress" />
              </SectionCard>
            )}
          </div>
        )}

        {(block.user_level?.engineer_wise?.length ?? 0) > 0 && (
          <SectionCard title={`${title} — Engineer Level`} icon={FaMapMarkerAlt}>
            <DataTable
              columns={pivotedCols('name', 'Engineer', ENGINEER_PROGRESS_KEYS, PROGRESS_LABELS)}
              data={block.user_level.engineer_wise}
              maxHeight="300px"
              emptyMessage="No engineer activity"
            />
          </SectionCard>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* ===== Responsibility Overview ===== */}
      {responsibility && (
        <SectionCard title="Responsibility Overview" icon={FaProjectDiagram}>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            <StatCard title="Assigned Projects" value={responsibility.assigned_projects ?? 0} icon={FaProjectDiagram} iconBgColor="bg-blue-100" iconColor="text-blue-600" />
            <StatCard title="Direct Team" value={responsibility.direct_team ?? 0} icon={FaUsers} iconBgColor="bg-indigo-100" iconColor="text-indigo-600" />
            <StatCard title="All Team" value={responsibility.all_team ?? 0} icon={FaUsers} iconBgColor="bg-violet-100" iconColor="text-violet-600" />
            <StatCard title="Survey Done (Month)" value={responsibility.survey_done_current_month ?? 0} icon={FaCheckDouble} iconBgColor="bg-green-100" iconColor="text-green-600" />
            <StatCard title="Site Created" value={responsibility.site_created ?? 0} icon={FaMapMarkerAlt} iconBgColor="bg-gray-100" iconColor="text-gray-600" />
            <StatCard title="Engineer Assigned" value={responsibility.engineer_assigned ?? 0} icon={FaUserTie} iconBgColor="bg-orange-100" iconColor="text-orange-600" />
            <StatCard title="Field Work In Progress" value={responsibility.field_work_in_progress ?? 0} icon={FaCog} iconBgColor="bg-purple-100" iconColor="text-purple-600" />
            <StatCard title="Site Data WIP" value={responsibility.site_data_wip ?? 0} icon={FaFileAlt} iconBgColor="bg-teal-100" iconColor="text-teal-600" />
          </div>
        </SectionCard>
      )}

      {/* ===== Approvals ===== */}
      <ApprovalsSection approvals={approvals} />

      {/* ===== Pending Work ===== */}
      {pendingWork && (
        <SectionCard title="Pending Work — Group Stage" icon={FaHourglassHalf}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MiniStat label="Survey Pending" value={pendingWork.group_stage?.survey_pending ?? 0} color="blue" />
            <MiniStat label="Field Data Upload Pending" value={pendingWork.group_stage?.field_data_upload_pending ?? 0} color="yellow" />
          </div>
          {pendingWork.by_workflow_status && (
            <div className="mt-4 h-64">
              <BarChart
                labels={Object.keys(pendingWork.by_workflow_status).map((k) => PENDING_LABELS[k] ?? k)}
                datasets={[{ label: 'Sites', data: Object.values(pendingWork.by_workflow_status).map((v) => toNumber(v)) }]}
                horizontal
                showLegend={false}
              />
            </div>
          )}
        </SectionCard>
      )}

      {pendingWork && (pendingActivity.length > 0 || pendingProject.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {pendingActivity.length > 0 && (
            <SectionCard title="Pending Work — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={flatCols('Activity', PENDING_STATUSES, PENDING_LABELS)} data={pendingActivity} maxHeight="320px" emptyMessage="No pending sites" />
            </SectionCard>
          )}
          {pendingProject.length > 0 && (
            <SectionCard title="Pending Work — Project Level" icon={FaBuilding}>
              <DataTable columns={flatCols('Project', PENDING_STATUSES, PENDING_LABELS)} data={pendingProject} maxHeight="320px" emptyMessage="No pending sites" />
            </SectionCard>
          )}
        </div>
      )}

      {pendingEngineer.length > 0 && (
        <SectionCard title="Pending Work — Engineer Level" subtitle="Field stages by engineer" icon={FaMapMarkerAlt}>
          <DataTable columns={flatCols('Engineer', ENGINEER_PENDING_STATUSES, PENDING_LABELS)} data={pendingEngineer} maxHeight="320px" emptyMessage="No engineer field work pending" />
        </SectionCard>
      )}

      {/* ===== Today / Yesterday progress ===== */}
      {renderProgress(todayProgress, "Today's Progress", FaCheckDouble)}
      {renderProgress(yesterdayProductivity, 'Productivity (Yesterday)', FaCalendarCheck)}

      {/* ===== Rejection Pending ===== */}
      {rejectionPending && (
        <SectionCard title="Rejection Pending — Workflow Stage" icon={FaExclamationTriangle}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <MiniStat label="TA Rejected" value={rejectionPending.by_workflow_status?.ta_rejected ?? 0} color="red" />
            <MiniStat label="Report Maker Rejected" value={rejectionPending.by_workflow_status?.report_maker_rejected ?? 0} color="red" />
            <MiniStat label="Validation Rejected" value={rejectionPending.by_workflow_status?.validation_rejected ?? 0} color="red" />
          </div>
        </SectionCard>
      )}

      {rejectionPending && (rejectActivity.length > 0 || rejectProject.length > 0) && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {rejectActivity.length > 0 && (
            <SectionCard title="Rejections — Activity Level" icon={FaProjectDiagram}>
              <DataTable columns={flatCols('Activity', REJECT_STATUSES, REJECT_LABELS)} data={rejectActivity} maxHeight="280px" emptyMessage="No rejections" />
            </SectionCard>
          )}
          {rejectProject.length > 0 && (
            <SectionCard title="Rejections — Project Level" icon={FaBuilding}>
              <DataTable columns={flatCols('Project', REJECT_STATUSES, REJECT_LABELS)} data={rejectProject} maxHeight="280px" emptyMessage="No rejections" />
            </SectionCard>
          )}
        </div>
      )}

      {rejectionPending?.user_level && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <SectionCard title="Report Makers — Rejected" icon={FaUserTie}>
            <DataTable columns={[{ key: 'report_designer_username', label: 'Report Maker' }, { key: 'count', label: 'Rejected', align: 'center' }]} data={rejectionPending.user_level.report_maker_wise ?? []} maxHeight="240px" emptyMessage="None" />
          </SectionCard>
          <SectionCard title="Validators — Rejected" icon={FaUserTie}>
            <DataTable columns={[{ key: 'validator_username', label: 'Validator' }, { key: 'count', label: 'Rejected', align: 'center' }]} data={rejectionPending.user_level.validator_wise ?? []} maxHeight="240px" emptyMessage="None" />
          </SectionCard>
          <SectionCard title="TA — Rejected" icon={FaUserTie}>
            <DataTable columns={[{ key: 'ta_username', label: 'TA' }, { key: 'count', label: 'Rejected', align: 'center' }]} data={rejectionPending.user_level.ta_wise ?? []} maxHeight="240px" emptyMessage="None" />
          </SectionCard>
        </div>
      )}

      {/* ===== Reference lists ===== */}
      {referenceLists && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          <SectionCard title="Projects" icon={FaProjectDiagram}>
            <DataTable columns={[{ key: 'project_short_name', label: 'Code' }, { key: 'project_name', label: 'Project' }]} data={referenceLists.project_names ?? []} maxHeight="220px" emptyMessage="No projects" />
          </SectionCard>
          <SectionCard title="TAs" icon={FaUserTie}>
            <DataTable columns={[{ key: 'ta_username', label: 'TA' }, { key: 'count', label: 'Sites', align: 'center' }]} data={referenceLists.ta_names ?? []} maxHeight="220px" emptyMessage="No TAs" />
          </SectionCard>
          <SectionCard title="MIS" icon={FaUserTie}>
            <DataTable columns={[{ key: 'mis_username', label: 'MIS' }, { key: 'count', label: 'Sites', align: 'center' }]} data={referenceLists.mis_names ?? []} maxHeight="220px" emptyMessage="No MIS" />
          </SectionCard>
          <SectionCard title="Engineers" icon={FaMapMarkerAlt}>
            <DataTable columns={[{ key: 'site_engineer_username', label: 'Engineer' }, { key: 'count', label: 'Sites', align: 'center' }]} data={referenceLists.engineer_names ?? []} maxHeight="220px" emptyMessage="No engineers" />
          </SectionCard>
          <SectionCard title="Last 10 Sites Completed" icon={FaCheckDouble}>
            <DataTable
              columns={[
                { key: 'site_id', label: 'Site ID' },
                { key: 'site_name', label: 'Name' },
                { key: 'work_date', label: 'Date', render: (item: any) => formatDate(item.work_date) },
              ]}
              data={referenceLists.last_10_completed ?? []}
              maxHeight="220px"
              emptyMessage="No completed sites"
            />
          </SectionCard>
        </div>
      )}
    </div>
  );
};

export default TeamCoordinatorSpecSections;